import { ProductAddOnStorefrontVisibility } from '@/lib/enums/ProductAddOnStorefrontVisibility'
import { ProductStorefrontVisibility } from '@/lib/enums/ProductStorefrontVisibility'
import { ProductCatalogType } from '@/lib/product-catalog-type'
import type { ProductRole } from '@/lib/product-role'
import type { Collection } from '@/lib/services/collections/collections.types'
import {
  defaultSubscriptionFrequencyOptions,
  subscriptionFrequencyOptions,
} from '@/lib/subscription-frequency'
import type {
  ProductAddOnStorefrontVisibility as ProductAddOnStorefrontVisibilityValue,
  ProductCatalogType as ProductCatalogTypeValue,
  ProductStorefrontVisibility as ProductStorefrontVisibilityValue,
  SubscriptionFrequency,
} from './products.types'

export const defaultSubscriptionDisclaimer =
  'We strive to fulfill your order as pictured; however, substitutions may be necessary due to flower availability. When substitutions are made, we match color and size as closely as possible and always use blooms of equal or greater value.'

export const defaultSubscriptionDisclaimerHtml = `<p>${defaultSubscriptionDisclaimer}</p>`

export const productRoles: ProductRole[] = [
  'product',
  'standalone_add_on',
  'add_on_only',
  'subscription_package',
]

type IdList = readonly (number | string)[] | null | undefined

function normalizeIds(value: IdList) {
  return (Array.isArray(value) ? value : [])
    .map((id) => Number(id))
    .filter((id) => Number.isInteger(id) && id > 0)
}

function normalizeSubscriptionFrequencies(value: readonly string[] | null | undefined) {
  const selectedValues = new Set(Array.isArray(value) ? value.map(String) : [])

  return subscriptionFrequencyOptions
    .map((option) => option.id)
    .filter((frequency) => selectedValues.has(frequency))
}

export function mapProductVisibilityToAddOnVisibility(
  visibility: ProductStorefrontVisibilityValue | null | undefined
): ProductAddOnStorefrontVisibilityValue {
  return visibility === ProductStorefrontVisibility.Hidden.Value
    ? ProductAddOnStorefrontVisibility.Hidden.Value
    : ProductAddOnStorefrontVisibility.Always.Value
}

export function mapAddOnVisibilityToProductVisibility(
  visibility: ProductAddOnStorefrontVisibilityValue | null | undefined
): ProductStorefrontVisibilityValue {
  return visibility === ProductAddOnStorefrontVisibility.Always.Value
    ? ProductStorefrontVisibility.Always.Value
    : ProductStorefrontVisibility.Hidden.Value
}

/** What an add-on-only product hides, kept so switching back restores it. */
export interface ProductRoleTransitionMemory {
  collections: Collection[]
  collectionsWereEdited: boolean
  occasionIds: number[]
  searchTagIds: number[]
}

/** The product form values a role change reads. */
export interface ProductRoleTransitionValues {
  product_role?: ProductRole
  collections?: Collection[]
  occasion_ids?: IdList
  search_tag_ids?: IdList
  storefront_visibility?: ProductStorefrontVisibilityValue
  add_on_storefront_visibility?: ProductAddOnStorefrontVisibilityValue
  subscription_frequency_options?: readonly string[] | null
  subscription_disclaimer?: string | null
  subscription_disclaimer_html?: string | null
  /** Whether the rich text disclaimer is blank once normalized; the form works it out. */
  subscription_disclaimer_html_is_empty?: boolean
  /** The disclaimer HTML to fill in when it is blank. */
  subscription_disclaimer_default_html?: string | null
}

/** The product form fields a role change sets. */
export interface ProductRoleTransitionPatch {
  product_catalog_type?: ProductCatalogTypeValue
  storefront_visibility?: ProductStorefrontVisibilityValue
  add_on_storefront_visibility?: ProductAddOnStorefrontVisibilityValue
  occasion_ids?: number[]
  search_tag_ids?: number[]
  collections?: Collection[]
  subscription_frequency_options?: SubscriptionFrequency[]
  subscription_disclaimer?: string
  subscription_disclaimer_html?: string | null
  is_designers_choice?: boolean
}

export interface ProductRoleTransition {
  patch: ProductRoleTransitionPatch
  memory: ProductRoleTransitionMemory
  collectionsWereEdited: boolean
}

export function createProductRoleTransitionMemory(
  values: ProductRoleTransitionValues | null | undefined,
  collectionsWereEdited = false
): ProductRoleTransitionMemory {
  const productRole = values?.product_role ?? 'product'
  const isAddOnOnly = productRole === 'add_on_only'

  return {
    collections: values?.collections ?? [],
    collectionsWereEdited: isAddOnOnly ? false : collectionsWereEdited,
    occasionIds: isAddOnOnly ? [] : normalizeIds(values?.occasion_ids),
    searchTagIds: isAddOnOnly ? [] : normalizeIds(values?.search_tag_ids),
  }
}

export function transitionProductRole({
  values,
  previousRole,
  nextRole,
  memory,
  collectionsWereEdited = false,
}: {
  values: ProductRoleTransitionValues | null | undefined
  previousRole: ProductRole
  nextRole: ProductRole
  memory?: Partial<ProductRoleTransitionMemory> | null
  collectionsWereEdited?: boolean
}): ProductRoleTransition {
  const nextMemory: ProductRoleTransitionMemory = {
    ...createProductRoleTransitionMemory({}, false),
    ...memory,
  }
  const patch: ProductRoleTransitionPatch = {}
  let nextCollectionsWereEdited = collectionsWereEdited

  if (previousRole === nextRole) {
    return {
      patch,
      memory: nextMemory,
      collectionsWereEdited: nextCollectionsWereEdited,
    }
  }

  const currentProductVisibility =
    values?.storefront_visibility ?? ProductStorefrontVisibility.PublishedCollection.Value
  const currentAddOnVisibility =
    values?.add_on_storefront_visibility ?? ProductAddOnStorefrontVisibility.Hidden.Value

  patch.product_catalog_type =
    nextRole === 'subscription_package'
      ? ProductCatalogType.SubscriptionPackage
      : ProductCatalogType.Standard

  if (nextRole === 'subscription_package') {
    if (previousRole === 'add_on_only') {
      patch.storefront_visibility = mapAddOnVisibilityToProductVisibility(currentAddOnVisibility)
      patch.occasion_ids = nextMemory.occasionIds
      patch.search_tag_ids = nextMemory.searchTagIds
      patch.collections = nextMemory.collections
      nextCollectionsWereEdited = nextMemory.collectionsWereEdited
    }

    patch.add_on_storefront_visibility = ProductAddOnStorefrontVisibility.Hidden.Value

    if (normalizeSubscriptionFrequencies(values?.subscription_frequency_options).length === 0) {
      patch.subscription_frequency_options = defaultSubscriptionFrequencyOptions
    }

    if (!String(values?.subscription_disclaimer ?? '').trim()) {
      patch.subscription_disclaimer = defaultSubscriptionDisclaimer
    }

    const disclaimerHtmlIsEmpty =
      values?.subscription_disclaimer_html_is_empty ??
      !String(values?.subscription_disclaimer_html ?? '').trim()

    if (disclaimerHtmlIsEmpty) {
      patch.subscription_disclaimer_html =
        values?.subscription_disclaimer_default_html ?? defaultSubscriptionDisclaimerHtml
    }

    return {
      patch,
      memory: nextMemory,
      collectionsWereEdited: nextCollectionsWereEdited,
    }
  }

  if (nextRole === 'product') {
    if (previousRole === 'add_on_only') {
      patch.storefront_visibility = mapAddOnVisibilityToProductVisibility(currentAddOnVisibility)
      patch.occasion_ids = nextMemory.occasionIds
      patch.search_tag_ids = nextMemory.searchTagIds
      patch.collections = nextMemory.collections
      nextCollectionsWereEdited = nextMemory.collectionsWereEdited
    }

    patch.add_on_storefront_visibility = ProductAddOnStorefrontVisibility.Hidden.Value

    return {
      patch,
      memory: nextMemory,
      collectionsWereEdited: nextCollectionsWereEdited,
    }
  }

  if (previousRole === 'product' || previousRole === 'subscription_package') {
    patch.add_on_storefront_visibility =
      mapProductVisibilityToAddOnVisibility(currentProductVisibility)
  }

  if (nextRole === 'add_on_only') {
    nextMemory.collections = values?.collections ?? []
    nextMemory.collectionsWereEdited = collectionsWereEdited
    nextMemory.occasionIds = normalizeIds(values?.occasion_ids)
    nextMemory.searchTagIds = normalizeIds(values?.search_tag_ids)
    patch.is_designers_choice = false
    patch.storefront_visibility = ProductStorefrontVisibility.Hidden.Value
    patch.occasion_ids = []
    patch.search_tag_ids = []

    return {
      patch,
      memory: nextMemory,
      collectionsWereEdited: nextCollectionsWereEdited,
    }
  }

  if (previousRole === 'add_on_only') {
    patch.storefront_visibility = mapAddOnVisibilityToProductVisibility(currentAddOnVisibility)
    patch.occasion_ids = nextMemory.occasionIds
    patch.search_tag_ids = nextMemory.searchTagIds
    patch.collections = nextMemory.collections
    nextCollectionsWereEdited = nextMemory.collectionsWereEdited
  }

  return {
    patch,
    memory: nextMemory,
    collectionsWereEdited: nextCollectionsWereEdited,
  }
}
